import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';

import { StaticContext, TabletScreen, MobileScreen } from '@sm/webassets';
import { defineMessages, T, t } from '@sm/intl';
import {
  Typography,
  Accordion,
  AccordionItem,
  List,
  ListItem
} from '@sm/wds-react';
import { getClientEnvironmentDetails } from '@sm/utils';

import './navbar.scss';
import publishedApplicationListingCategoriesQuery from './NavBar.graphql';
import SidebarSkeleton from './SideBarSkeleton';
// LinkWithSearch should be used so it will keep any existing query arguments present in the url (needed for ut_source)
import LinkWithSearch from '../LinkWithSearch';

const COPY = defineMessages({
  NAVBAR_APPS_DIRECTORY: {
    id: 'AppsDirectory.NavBar.AppsDirectory',
    defaultMessage: 'App Directory',
    description: `[Type: Header][Vis.: high] - A section title for a group of links on the page's sidebar`
  },
  NAVBAR_CATEGORIES: {
    id: 'AppsDirectory.NavBar.Categories',
    defaultMessage: 'App Category',
    description: `[Type: Header][Vis.: high] - A section title for a group of links on the page's sidebar`
  },
  NAVBAR_MY_APPS: {
    id: 'AppsDirectory.NavBar.MyApps',
    defaultMessage: 'My Apps',
    description: `[Type: Link][Vis.: med] - A link on the sidebar directing the user to its installed apps`
  },
  NAVBAR_FEATURED: {
    id: 'AppsDirectory.NavBar.Featured',
    defaultMessage: 'Featured',
    description: `[Type: Link][Vis.: med] - A link on the sidebar directing the user the featured apps`
  },
  NAVBAR_MOST_POPULAR: {
    id: 'AppsDirectory.NavBar.MostPopular',
    defaultMessage: 'Most Popular',
    description: `[Type: Link][Vis.: med] - A link on the sidebar directing the user to the most popular apps`
  },
  NAVBAR_RECENTLY_ADDED: {
    id: 'AppsDirectory.NavBar.RecentlyAdded',
    defaultMessage: 'Recently Added',
    description: `[Type: Link][Vis.: med] - A link on the sidebar directing the user to the most recently added apps`
  }
});

const SidebarLink = ({ target, text }) => (
  <li className="sm-navbar__group-item">
    <LinkWithSearch pathname={target} className="sm-navbar__group-item-link">
      {text}
    </LinkWithSearch>
  </li>
);

SidebarLink.propTypes = {
  target: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired
};

const NavBar = props => {
  const {
    environment: { languageId },
    user,
    userAgent
  } = useContext(StaticContext);
  const { isDesktop } = getClientEnvironmentDetails(userAgent);
  return (
    <div {...props}>
      <Query
        query={publishedApplicationListingCategoriesQuery}
        variables={{ language: languageId }}
      >
        {({ loading, data, error }) => {
          return (
            <>
              <MobileScreen defaultMatches={!isDesktop}>
                <Accordion className="wds-m-b-4 sm-navbar__accordion">
                  <AccordionItem dense title={t(COPY.NAVBAR_APPS_DIRECTORY)}>
                    <List dense transparent>
                      <Choose>
                        <When condition={user.isAuthenticated}>
                          <ListItem>
                            <LinkWithSearch pathname="/apps/my-apps">
                              <Typography>
                                <T desc={COPY.NAVBAR_MY_APPS} />
                              </Typography>
                            </LinkWithSearch>
                          </ListItem>
                        </When>
                        <Otherwise>
                          <span />
                        </Otherwise>
                      </Choose>
                      <ListItem>
                        <LinkWithSearch pathname="/apps/featured-apps">
                          <Typography>
                            <T desc={COPY.NAVBAR_FEATURED} />
                          </Typography>
                        </LinkWithSearch>
                      </ListItem>
                      <ListItem>
                        <LinkWithSearch pathname="/apps/most-popular">
                          <Typography>
                            <T desc={COPY.NAVBAR_MOST_POPULAR} />
                          </Typography>
                        </LinkWithSearch>
                      </ListItem>
                      <ListItem>
                        <LinkWithSearch pathname="/apps/recently-added">
                          <Typography>
                            <T desc={COPY.NAVBAR_RECENTLY_ADDED} />
                          </Typography>
                        </LinkWithSearch>
                      </ListItem>
                    </List>
                  </AccordionItem>
                  <AccordionItem title={t(COPY.NAVBAR_CATEGORIES)}>
                    <Choose>
                      <When condition={!loading && data}>
                        <List transparent>
                          {data.publishedApplicationListingCategories.map(
                            cat => (
                              <ListItem key={cat.id}>
                                <LinkWithSearch
                                  pathname={`/apps/categories/${cat.key}`}
                                >
                                  <Typography>{cat.name}</Typography>
                                </LinkWithSearch>
                              </ListItem>
                            )
                          )}
                        </List>
                      </When>
                      <Otherwise>
                        <span />
                      </Otherwise>
                    </Choose>
                  </AccordionItem>
                </Accordion>
              </MobileScreen>
              <TabletScreen defaultMatches={isDesktop}>
                <Typography
                  component="div"
                  variant="cardTitle"
                  className="sm-navbar__title"
                >
                  <T desc={COPY.NAVBAR_APPS_DIRECTORY} />
                </Typography>
                <ul className="sm-navbar__group wds-m-t-3 wds-m-b-3">
                  <If condition={user.isAuthenticated}>
                    <SidebarLink
                      target="/apps/my-apps"
                      text={t(COPY.NAVBAR_MY_APPS)}
                    />
                  </If>
                  <SidebarLink
                    target="/apps/featured-apps"
                    text={t(COPY.NAVBAR_FEATURED)}
                  />
                  <SidebarLink
                    target="/apps/most-popular"
                    text={t(COPY.NAVBAR_MOST_POPULAR)}
                  />
                  <SidebarLink
                    target="/apps/recently-added"
                    text={t(COPY.NAVBAR_RECENTLY_ADDED)}
                  />
                </ul>
                <Typography
                  component="div"
                  variant="cardTitle"
                  className="sm-navbar__title"
                >
                  <T desc={COPY.NAVBAR_CATEGORIES} />
                </Typography>
                <Choose>
                  <When condition={loading}>
                    <SidebarSkeleton />
                  </When>
                  <When condition={error}>null</When>
                  <Otherwise>
                    <ul className="sm-navbar__group sm-navbar__categories">
                      {data.publishedApplicationListingCategories.map(cat => (
                        <SidebarLink
                          key={cat.id}
                          target={`/apps/categories/${cat.key}`}
                          text={cat.name}
                        />
                      ))}
                    </ul>
                  </Otherwise>
                </Choose>
              </TabletScreen>
            </>
          );
        }}
      </Query>
    </div>
  );
};

export default NavBar;
